import { AsyncPipe, DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { SubSink } from 'subsink';
import { UserService } from '../../../admin/services/user.service';
import { User } from '../../../admin/models/user';
import { BehaviorSubject } from 'rxjs';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { Role as RoleConstants } from '../../../admin/constants/role';
import { Role } from '../../../admin/models/role';

@Component({
    selector: 'app-login-button',
    templateUrl: './login-button.component.html',
    styleUrl: './login-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SvgIconComponent, AsyncPipe],
})
export class LoginButtonComponent implements OnInit, OnDestroy {
    document = inject<Document>(DOCUMENT);
    auth = inject(AuthService);
    private userService = inject(UserService);
    private mixpanelService = inject(MixpanelService);

    user?: User;

    private sub = new SubSink();
    isLoadingUserInformation$ = new BehaviorSubject<boolean>(false);

    get fullName(): string {
        return !!this.user
            ? `${this.user.firstName} ${this.user.lastName}`
            : '';
    }

    get isSuperAdministrator(): boolean {
        return (
            this.user?.roles?.some(
                (role) => role.name === RoleConstants.SuperAdministrator,
            ) ?? false
        );
    }

    get userRoles(): Role[] {
        const roles =
            this.user?.roles?.filter(
                (x) => x.name != RoleConstants.SuperAdministrator,
            ) ?? [];
        return roles.filter(
            (role, index) =>
                roles.findIndex((r) => r.name === role.name) === index,
        );
    }

    ngOnInit(): void {
        this.isLoadingUserInformation$.next(true);
        this.sub.sink = this.userService.getCurrentUser().subscribe((user) => {
            this.user = user;
            this.isLoadingUserInformation$.next(false);
        });
    }

    logoutUser() {
        sessionStorage.clear();
        this.signOut();
        this.auth.logout({
            logoutParams: { returnTo: document.location.origin },
        });
    }

    signOut() {
        const userEmail = localStorage.getItem('userEmail') || '';
        this.mixpanelService.track({
            id: userEmail,
            eventName: 'Sign out',
            eventDisplayName: 'Sign out',
            kpi: 'Click-Through Rate',
            trigger: 'On Button Click',
            eventDefinition: 'User clicks the sign-out button',
            propertyType: 'Event Property',
            propertyDefinition: 'Button Label Signout',
        });
        this.mixpanelService.reset();
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }
}
